import type { ReactElement, ReactNode } from 'react'
import { Box, useTheme } from '@mui/material'

const dateTimeFormatter = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'medium',
  timeStyle: 'short',
})
const timeFormatter = new Intl.DateTimeFormat('de-DE', { timeStyle: 'short' })

export type FileListRowProps = {
  icon?: ReactNode
  /** A plain string gets the default single-line ellipsis styling; pass a node for custom titles. */
  title: ReactNode
  subtitle?: ReactNode
  /** ISO timestamp (e.g. upload time) appended to the subtitle line. */
  timestamp?: string
  /** Render `timestamp` as date+time, or time only (e.g. when the date is shown by a group header). */
  timestampFormat?: 'datetime' | 'time'
  /** Small element (e.g. a chip) at the end of the subtitle line. */
  badge?: ReactNode
  actions?: ReactNode
  selected?: boolean
  onClick?: () => void
  'data-cy'?: string
}

/**
 * List row for file-like entries (documents, plans): icon, title + subtitle, trailing actions.
 * Matches the document list style used in Results2's Dokumente tab.
 */
export const FileListRow = ({
  icon,
  title,
  subtitle,
  timestamp,
  timestampFormat = 'datetime',
  badge,
  actions,
  selected = false,
  onClick,
  'data-cy': dataCy,
}: FileListRowProps): ReactElement => {
  const theme = useTheme()

  const formattedTimestamp = timestamp
    ? (timestampFormat === 'time' ? timeFormatter : dateTimeFormatter).format(new Date(timestamp))
    : null

  return (
    <Box
      onClick={onClick}
      data-cy={dataCy}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1,
        py: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
        transition: 'background 120ms',
        cursor: onClick ? 'pointer' : undefined,
        background: selected ? theme.palette.action.selected : undefined,
        '&:hover': {
          background: selected ? theme.palette.action.selected : theme.palette.action.hover,
        },
      }}
    >
      {icon}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {typeof title === 'string' ? (
          <Box
            sx={{
              fontSize: 13,
              height: '19px',
              lineHeight: '19px',
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Box>
        ) : (
          title
        )}
        {subtitle || formattedTimestamp || badge ? (
          <Box
            sx={{
              fontSize: 11,
              color: 'text.secondary',
              mt: 0.25,
            }}
          >
            {subtitle}
            {subtitle && formattedTimestamp ? ' · ' : null}
            {formattedTimestamp}
            {badge}
          </Box>
        ) : null}
      </Box>
      {actions ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, mr: -0.5 }}>{actions}</Box>
      ) : null}
    </Box>
  )
}
